import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOrders } from "@/lib/api/hooks";
import * as api from "@/lib/api/endpoints";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Order } from "@/lib/api/types";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import { PermissionGuard } from "@/components/guards/PermissionGuard";

export default function StoreOrderDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { updateStatus } = useOrders();

    const [order, setOrder] = useState<Order | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        const fetchOrder = async () => {
            if (!id) return;
            try {
                setIsLoading(true);
                const data = await api.orders.get(id);
                setOrder(data);
            } catch (error) {
                toast.error("Failed to load order");
                navigate("/store/orders");
            } finally {
                setIsLoading(false);
            }
        };

        fetchOrder();
    }, [id, navigate]);

    const [trackingNumber, setTrackingNumber] = useState("");

    const handleStatusChange = async (newStatus: string) => {
        if (!order) return;
        let refundConfirmed: boolean | undefined;
        if (newStatus === 'cancelled') {
            if (order.paymentStatus === 'paid') {
                // The server refuses to cancel a paid order without this (ORD-03)
                refundConfirmed = window.confirm(
                    `This order is PAID (${formatPrice(order.totalAmount)}).\n\nRefund it in the Paystack dashboard first.\n\nHave you refunded it? Cancelling returns the items to stock.`
                );
                if (!refundConfirmed) return;
            } else if (!window.confirm('Cancel this unpaid order and return its items to stock?')) {
                return;
            }
        }
        try {
            setIsUpdating(true);
            const result = await updateStatus({
                id: order.id,
                status: newStatus,
                trackingNumber: newStatus === 'shipped' ? trackingNumber.trim() || undefined : undefined,
                refundConfirmed,
            });
            setOrder(prev => prev ? ({ ...prev, ...(result?.order ?? { status: newStatus }) }) : null);
            setTrackingNumber("");
        } catch (error) {
            // The reason is shown by the API error toast
        } finally {
            setIsUpdating(false);
        }
    };

    // Next steps allowed by the server (ORD-03/ORD-12): fulfilment only for paid orders
    const paid = order?.paymentStatus === 'paid';
    const nextActions: { status: string; label: string; variant?: 'destructive' | 'outline' }[] = !order ? [] : ({
        pending: [{ status: 'cancelled', label: 'Cancel order', variant: 'destructive' }],
        processing: [
            ...(paid ? [{ status: 'shipped', label: 'Mark shipped' }] : []),
            { status: 'cancelled', label: paid ? 'Cancel & refund' : 'Cancel order', variant: 'destructive' },
        ],
        shipped: [{ status: 'delivered', label: 'Mark delivered' }, { status: 'returned', label: 'Mark returned', variant: 'outline' }],
        delivered: [{ status: 'returned', label: 'Mark returned', variant: 'outline' }],
    } as Record<string, any[]>)[order.status] ?? [];

    const formatPrice = (priceInCents: number) => {
        return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(priceInCents / 100);
    };

    if (isLoading || !order) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-12">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-4 w-full sm:w-auto">
                    <Button variant="ghost" size="icon" onClick={() => navigate("/store/orders")}>
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-2xl font-bold tracking-tight">Order #{order.orderNumber}</h1>
                        <Badge variant="outline" className="text-muted-foreground">
                            {format(new Date(order.createdAt), "MMM d, yyyy h:mm a")}
                        </Badge>
                    </div>
                </div>

                <PermissionGuard resource="orders" action="update">
                    <div className="flex flex-col items-start gap-2 sm:items-end">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-muted-foreground">Status:</span>
                            <Badge variant="outline" className="capitalize">{order.status}</Badge>
                        </div>
                        {nextActions.some(a => a.status === 'shipped') && (
                            <Input
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                                placeholder="Tracking number (optional)"
                                className="w-[220px]"
                                maxLength={100}
                            />
                        )}
                        <div className="flex flex-wrap gap-2">
                            {nextActions.map(action => (
                                <Button
                                    key={action.status}
                                    size="sm"
                                    variant={action.variant ?? 'default'}
                                    disabled={isUpdating}
                                    onClick={() => handleStatusChange(action.status)}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </div>
                        {order.status === 'processing' && !paid && (
                            <p className="text-xs text-muted-foreground">Can be shipped once paid.</p>
                        )}
                        {order.status === 'pending' && (
                            <p className="text-xs text-muted-foreground">Awaiting payment. Unpaid orders are released after an hour.</p>
                        )}
                    </div>
                </PermissionGuard>
            </div>

            {order.paymentStatus === 'refund_due' && (
                <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-800" role="alert">
                    <strong>Refund needed.</strong> Payment arrived after this order was cancelled or had expired, and its
                    items were already back in stock. Refund {formatPrice(order.totalAmount)} in the Paystack dashboard
                    {order.paymentReference ? <> (reference <span className="font-mono">{order.paymentReference}</span>)</> : null}.
                </div>
            )}

            <div className="grid gap-6 lg:grid-cols-3">
                {/* Main Content: Items */}
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Order Items</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-6">
                                {order.items?.map((item) => (
                                    <div key={item.id} className="flex gap-4">
                                        <div className="h-20 w-20 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                                            {item.productImage ? (
                                                <img src={item.productImage} alt={item.productName} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-muted">
                                                    <div className="w-8 h-8 bg-muted-foreground/20 rounded" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-medium text-base">{item.productName}</h4>
                                            {item.variantDetails && (
                                                <div className="text-sm text-muted-foreground mt-1">
                                                    {item.variantDetails.size && <span className="mr-3">Size: {item.variantDetails.size}</span>}
                                                    {item.variantDetails.color && <span>Color: {item.variantDetails.color}</span>}
                                                </div>
                                            )}
                                            <div className="flex justify-between items-center mt-2">
                                                <span className="text-sm text-muted-foreground">
                                                    Qty: {item.quantity} × {formatPrice(item.productPrice)}
                                                </span>
                                                <span className="font-medium">{formatPrice(item.subtotal)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Separator className="my-6" />

                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Subtotal</span>
                                    <span>{formatPrice(order.subtotal)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Shipping</span>
                                    <span>{formatPrice(order.shippingCost)}</span>
                                </div>
                                <Separator className="my-2" />
                                <div className="flex justify-between font-bold text-lg">
                                    <span>Total</span>
                                    <span>{formatPrice(order.totalAmount)}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Sidebar: Customer & Payment */}
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Customer</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div>
                                <p className="font-medium">{order.customerName}</p>
                                <p className="text-sm text-muted-foreground mt-1">{order.customerEmail}</p>
                                <p className="text-sm text-muted-foreground">{order.customerPhone}</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Delivery</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="text-sm leading-relaxed">
                                {order.deliveryLocation && (
                                    <div className="mb-4">
                                        <p className="font-medium mb-1">Delivery Location:</p>
                                        <p className="text-muted-foreground capitalize">
                                            {order.deliveryLocation.replace(/-/g, ' ')}
                                        </p>
                                    </div>
                                )}
                                <p className="font-medium mb-1">Shipping Address:</p>
                                <div className="text-muted-foreground">
                                    {order.shippingAddress.split('\n').map((line, i) => (
                                        <div key={i}>{line}</div>
                                    ))}
                                </div>
                            </div>
                            {order.trackingNumber && (
                                <div className="pt-3 border-t">
                                    <p className="text-sm font-medium">Tracking Number:</p>
                                    <p className="text-sm text-muted-foreground mt-1">{order.trackingNumber}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Payment</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex justify-between items-center p-3 bg-muted rounded-md">
                                <span className="text-sm font-medium">Status</span>
                                <Badge variant={order.paymentStatus === 'paid' ? 'default' : order.paymentStatus === 'failed' ? 'destructive' : 'secondary'}>
                                    {order.paymentStatus.toUpperCase()}
                                </Badge>
                            </div>
                            {order.paymentReference && (
                                <div className="text-xs text-muted-foreground text-center">
                                    Ref: {order.paymentReference}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
